import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { Icon, type IconName } from "./icons";

/* ---------------------------------------------------------------- field */

interface FieldContextValue {
  id: string;
  labelId: string;
  describedBy: string | undefined;
  invalid: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/** The id, description, and error state a control inside a Field picks up on its own. */
export function useFieldControl(id: string | undefined, invalid?: boolean): { id: string | undefined; labelId: string | undefined; "aria-describedby": string | undefined; "aria-invalid": true | undefined } {
  const field = useContext(FieldContext);
  return {
    id: id ?? field?.id,
    labelId: field?.labelId,
    "aria-describedby": field?.describedBy,
    "aria-invalid": invalid || field?.invalid ? true : undefined
  };
}

/** Animated error line shared by fields and inline editors. */
export function FieldError({ id, children }: { id?: string | undefined; children: ReactNode }) {
  return <AnimatePresence initial={false}>
    {children ? <motion.p
      key="error"
      id={id}
      className="ot-field__error"
      role="alert"
      initial={{ opacity: 0, height: 0, y: -4 }}
      animate={{ opacity: 1, height: "auto", y: 0 }}
      exit={{ opacity: 0, height: 0, transition: { duration: 0.14 } }}
    >
      <Icon name="warning" weight="bold"/>{children}
    </motion.p> : null}
  </AnimatePresence>;
}

export function Field({ label, hint, error, children, className = "", optional = false }: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null | undefined;
  children: ReactNode;
  className?: string;
  optional?: boolean;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return <FieldContext.Provider value={{ id, labelId: `${id}-label`, describedBy, invalid: Boolean(error) }}>
    <div className={`ot-field ${error ? "is-invalid" : ""} ${className}`.trim()}>
      <label className="ot-field__label" id={`${id}-label`} htmlFor={id}>{label}{optional && <span className="ot-field__optional">Optional</span>}</label>
      {children}
      {hint && !error && <p className="ot-field__hint" id={hintId}>{hint}</p>}
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  </FieldContext.Provider>;
}

/* ---------------------------------------------------------------- text */

export type InputSize = "md" | "sm";

interface Adornments {
  /** An icon or short text before the value, like a search glyph or "$". */
  leading?: ReactNode;
  /** Text or an icon after the value, like a currency code or "/h". */
  trailing?: ReactNode;
  invalid?: boolean | undefined;
  size?: InputSize;
  variant?: "field" | "ghost";
}

export function TextInput({ leading, trailing, invalid, size = "md", variant = "field", className = "", id, ref, ...props }: Adornments & Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & { ref?: Ref<HTMLInputElement> }) {
  const { labelId: _labelId, ...control } = useFieldControl(id, invalid);
  return <div className={`ot-input ot-input--${size} ot-input--${variant} ${control["aria-invalid"] ? "is-invalid" : ""} ${props.disabled ? "is-disabled" : ""} ${className}`.trim()}>
    {leading !== undefined && <span className="ot-input__leading">{leading}</span>}
    <input ref={ref} {...control} {...props}/>
    {trailing !== undefined && <span className="ot-input__trailing">{trailing}</span>}
  </div>;
}

export function TextArea({ invalid, className = "", id, ref, ...props }: { invalid?: boolean | undefined; ref?: Ref<HTMLTextAreaElement> } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { labelId: _labelId, ...control } = useFieldControl(id, invalid);
  return <div className={`ot-input ot-input--area ${control["aria-invalid"] ? "is-invalid" : ""} ${className}`.trim()}>
    <textarea ref={ref} rows={props.rows ?? 3} {...control} {...props}/>
  </div>;
}

type CommitResult = string | null | undefined | void;

/**
 * A text control that saves itself: it commits on blur or Enter, reverts on Escape, and shows a
 * brief check when the change lands. `onCommit` returns an error message to keep the draft open.
 */
export function EditableText({ value, onCommit, multiline = false, leading, trailing, size = "md", variant = "field", className = "", id, ...props }: Adornments & {
  value: string;
  onCommit: (next: string) => CommitResult | Promise<CommitResult>;
  multiline?: boolean;
  id?: string;
  placeholder?: string;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  type?: string;
  maxLength?: number;
  rows?: number;
  autoComplete?: string;
  "aria-label"?: string;
  className?: string;
}) {
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(0);
  const [resync, setResync] = useState(0);
  const focused = useRef(false);
  const skipCommit = useRef(false);
  const errorId = useId();

  useEffect(() => {
    if (!focused.current) setDraft(value);
  }, [value, resync]);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(0), 1400);
    return () => clearTimeout(timer);
  }, [saved]);

  const commit = async () => {
    if (draft === value) {
      setError(null);
      return;
    }
    const result = await onCommit(draft);
    if (typeof result === "string") {
      setError(result);
      return;
    }
    setError(null);
    setSaved(Date.now());
    setResync((tick) => tick + 1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      skipCommit.current = true;
      setDraft(value);
      setError(null);
      event.currentTarget.blur();
    } else if (event.key === "Enter" && (!multiline || event.metaKey || event.ctrlKey)) {
      // Commit here rather than on the blur that follows, so Enter saves even without focus events.
      event.preventDefault();
      skipCommit.current = true;
      void commit();
      event.currentTarget.blur();
    }
  };

  const shared = {
    id,
    value: draft,
    onChange: (event: { target: { value: string } }) => {
      setDraft(event.target.value);
      if (error) setError(null);
    },
    onFocus: () => {
      focused.current = true;
      skipCommit.current = false;
    },
    onBlur: () => {
      focused.current = false;
      if (skipCommit.current) {
        skipCommit.current = false;
        return;
      }
      void commit();
    },
    onKeyDown,
    invalid: Boolean(error),
    "aria-errormessage": error ? errorId : undefined,
    ...props
  };

  const tick = <AnimatePresence>
    {saved ? <motion.span
      key={saved}
      className="ot-saved"
      aria-hidden="true"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.2 } }}
    ><Icon name="check" weight="bold"/></motion.span> : null}
  </AnimatePresence>;

  return <div className={`ot-editable ${className}`.trim()}>
    {multiline
      ? <div className="ot-editable__area"><TextArea {...shared}/>{tick}</div>
      : <TextInput {...shared} size={size} variant={variant} leading={leading} trailing={<>{trailing}{tick}</>}/>}
    <FieldError id={errorId}>{error}</FieldError>
  </div>;
}

/* ---------------------------------------------------------------- toggles */

export function Switch({ checked, onChange, label, description, disabled = false, id, className = "", "aria-label": ariaLabel }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
  "aria-label"?: string;
}) {
  const control = useFieldControl(id);
  const descriptionId = useId();
  const button = <button
    type="button"
    role="switch"
    className={`ot-switch ${label ? "" : className}`.trim()}
    aria-checked={checked}
    aria-label={ariaLabel}
    aria-describedby={[control["aria-describedby"], description ? descriptionId : undefined].filter(Boolean).join(" ") || undefined}
    id={control.id}
    disabled={disabled}
    onClick={() => onChange(!checked)}
  ><span className="ot-switch__knob"/></button>;
  if (!label) return button;
  // A label around the button makes the whole row a target and names the switch.
  return <label className={`ot-toggle ${disabled ? "is-disabled" : ""} ${className}`.trim()}>
    <span className="ot-toggle__text">
      <span className="ot-toggle__label">{label}</span>
      {description && <span id={descriptionId} className="ot-toggle__description">{description}</span>}
    </span>
    {button}
  </label>;
}

export function Checkbox({ checked, onChange, label, round = false, disabled = false, className = "", "aria-label": ariaLabel }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  round?: boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}) {
  const box = <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    className={`ot-checkbox ${round ? "ot-checkbox--round" : ""} ${label ? "" : className}`.trim()}
    onClick={() => onChange(!checked)}
  >
    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 8.4 6.8 11 12 5.2"/></svg>
  </button>;
  if (!label) return box;
  return <label className={`ot-checkbox-label ${className}`.trim()}>{box}<span>{label}</span></label>;
}

/* ---------------------------------------------------------------- segmented */

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: IconName;
  /** Small trailing figure, like a count. */
  count?: number;
}

/** A pill row of choices; the selected pill slides between them. */
export function Segmented<T extends string>({ value, options, onChange, size = "md", stretch = false, className = "", id, "aria-label": ariaLabel }: {
  value: T;
  options: Array<SegmentOption<T>>;
  onChange: (value: T) => void;
  size?: InputSize;
  stretch?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}) {
  const pillId = useId();
  const control = useFieldControl(id);
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const index = Math.max(0, options.findIndex((option) => option.value === value));

  const move = (event: KeyboardEvent, delta: number) => {
    event.preventDefault();
    const next = (index + delta + options.length) % options.length;
    onChange(options[next]!.value);
    refs.current[next]?.focus();
  };

  return <div
    role="radiogroup"
    aria-label={ariaLabel}
    aria-labelledby={ariaLabel ? undefined : control.labelId}
    aria-describedby={control["aria-describedby"]}
    id={control.id}
    className={`ot-segmented ot-segmented--${size} ${stretch ? "ot-segmented--stretch" : ""} ${className}`.trim()}
    onKeyDown={(event) => {
      if (event.key === "ArrowRight" || event.key === "ArrowDown") move(event, 1);
      else if (event.key === "ArrowLeft" || event.key === "ArrowUp") move(event, -1);
    }}
  >
    {options.map((option, i) => {
      const on = option.value === value;
      return <button
        key={option.value}
        ref={(node) => { refs.current[i] = node; }}
        type="button"
        role="radio"
        aria-checked={on}
        tabIndex={on ? 0 : -1}
        className={`ot-segmented__option ${on ? "is-on" : ""}`}
        onClick={() => onChange(option.value)}
      >
        {on && <motion.span layoutId={pillId} className="ot-segmented__pill" aria-hidden="true" transition={{ type: "spring", stiffness: 520, damping: 38 }}/>}
        <span className="ot-segmented__label">
          {option.icon && <Icon name={option.icon}/>}
          {option.label}
          {option.count !== undefined && <span className="ot-segmented__count">{option.count}</span>}
        </span>
      </button>;
    })}
  </div>;
}
